/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from 'react-router-dom'
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../../firebase'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Link, List, ListItem, ListItemText, MenuItem, Paper, Select, TextField, Typography, useTheme, useMediaQuery } from "@mui/material"
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import { tokens } from "../../theme"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import { toast } from 'react-toastify'
import useCollectionSubscription from "../../components/hooks/useCollectionSubscription"
import useDocumentSubscription from "../../components/hooks/useDocumentSubscription"
import { useAuthState } from "../../components/hooks/useAuthState"
import { Customer } from "../../models/customer"
import { User } from "../../models/user"
import { buildSagawaCsvRow, decodeSagawaCsvFile, generateSagawaCsv, parseSagawaShippingHistoryCsv } from "./lib/sagawaCsv"
import { buildYamatoB2CsvRow, generateYamatoB2Csv, parseYamatoShippingResultCsv } from "./lib/yamatoCsv"

const ALL_CUSTOMERS = '__all__'

type IssueInvoicesResult = {
  created: Array<{ customer: string; invoiceId: number; reportUrl: string; amount: number; shipmentCount: number }>
  skipped: Array<{ customer: string; reason: string; shipmentCount: number }>
}

const issueFreeeInvoices = httpsCallable<{ shipmentIds: string[] }, IssueInvoicesResult>(functions, 'issueFreeeInvoices')

const getInitialDateRange = () => {
  const today = dayjs()
  const oneYearAgo = today.subtract(1, 'year')
  return { startDate: oneYearAgo, endDate: today }
}

const ShipmentList = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [items, setItems] = useState<Array<Record<string, any>>>([])
  const customers = useCollectionSubscription(collection(db, 'customers'), Customer)
  const { userId } = useAuthState()
  const currentUser = useDocumentSubscription<User>(
    userId ? doc(db, "users", userId) : null,
    User,
    [userId]
  )

  const handleDelete = async (id: string) => {
    if (!window.confirm("この出荷データを削除しますか？")) return
    try {
      const shipment = items.find((item) => item.id === id)
      const products: any[] = Array.isArray(shipment?.products) ? shipment.products : []
      await Promise.all(
        products.map((p) =>
          updateDoc(doc(db, "products", p.id), { isSold: false }).catch(() => undefined)
        )
      )
      await deleteDoc(doc(db, "shipments", id))
      toast.success("削除しました")
    } catch (e) {
      toast.error("削除に失敗しました: " + JSON.stringify(e))
    }
  }

  // freee請求書発行
  const [issuingInvoices, setIssuingInvoices] = useState<boolean>(false)
  const [invoiceResult, setInvoiceResult] = useState<IssueInvoicesResult | null>(null)

  const handleIssueInvoices = async () => {
    if (filteredItems.length === 0) {
      toast.warn("対象の出荷情報がありません")
      return
    }
    if (!window.confirm(`絞り込み結果（${filteredItems.length}件）の取引先ごとに、freeeへ下書き請求書を発行します。よろしいですか？`)) return

    setIssuingInvoices(true)
    try {
      const result = await issueFreeeInvoices({ shipmentIds: filteredItems.map((item: any) => item.id) })
      setInvoiceResult(result.data)
    } catch (e: any) {
      toast.error("請求書の発行に失敗しました: " + (e?.message || JSON.stringify(e)))
    } finally {
      setIssuingInvoices(false)
    }
  }

  // 佐川急便 e飛伝Ⅲ送り状CSV作成
  const handleCreateSagawaCsv = () => {
    if (filteredItems.length === 0) {
      toast.warn("対象の出荷情報がありません")
      return
    }
    if (!window.confirm(`絞り込み結果（${filteredItems.length}件）の送り状CSVを作成します。よろしいですか？`)) return

    const customerMap = new Map(customers.map((c: Customer) => [c.companyName, c]))
    const rows = filteredItems.map((item: any) => buildSagawaCsvRow(item, customerMap.get(item.customer)))
    const blob = generateSagawaCsv(rows)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `e飛伝送り状_${dayjs().format('YYYYMMDD')}.csv`)
    link.click()
    URL.revokeObjectURL(url)
  }

  // 出荷実績CSV取込（佐川・ヤマト共通 — ヘッダ先頭列でキャリアを自動判別）
  const shippingResultCsvInputRef = useRef<HTMLInputElement>(null)
  const [importingShippingResult, setImportingShippingResult] = useState<boolean>(false)

  const handleImportShippingResultClick = () => {
    shippingResultCsvInputRef.current?.click()
  }

  const handleImportShippingResultChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setImportingShippingResult(true)
    try {
      const text = await decodeSagawaCsvFile(file)

      // 先頭行の第1列でキャリアを判別（ダブルクォートを除去して比較）
      const firstCol = (text.split(/\r\n|\r|\n/)[0] || '')
        .split(',')[0]
        .replace(/^"|"$/g, '')
        .trim()

      let targets: Array<{ item: Record<string, any>; trackingNumber: string }>
      let carrierName: string

      if (firstCol === 'お問合せ送り状№') {
        const records = parseSagawaShippingHistoryCsv(text)
        if (records.length === 0) {
          toast.warn("CSVから有効なデータを読み取れませんでした")
          return
        }
        const recordMap = new Map(records.map((r) => [r.customerManagementNumber, r.trackingNumber]))
        targets = items
          .map((item) => ({ item, trackingNumber: recordMap.get((item.id || '').slice(0, 16)) }))
          .filter((t) => t.trackingNumber && t.trackingNumber !== t.item.trackingNumber) as
          Array<{ item: Record<string, any>; trackingNumber: string }>
        carrierName = '佐川急便'
      } else if (firstCol === 'お客様管理番号') {
        const records = parseYamatoShippingResultCsv(text)
        if (records.length === 0) {
          toast.warn("CSVから有効なデータを読み取れませんでした")
          return
        }
        const recordMap = new Map(records.map((r) => [r.customerManagementNumber, r.trackingNumber]))
        targets = items
          .map((item) => ({ item, trackingNumber: recordMap.get(item.id || '') }))
          .filter((t) => t.trackingNumber && t.trackingNumber !== t.item.trackingNumber) as
          Array<{ item: Record<string, any>; trackingNumber: string }>
        carrierName = 'ヤマト運輸'
      } else {
        toast.error(`対応していないCSV形式です（先頭列: ${firstCol || '（空）'}）`)
        return
      }

      if (targets.length === 0) {
        toast.info(`更新対象の出荷情報が見つかりませんでした（${carrierName}）`)
        return
      }

      if (!window.confirm(`${carrierName}のCSVを検出しました。${targets.length}件に宅配便番号を反映します。よろしいですか？`)) return

      await Promise.all(targets.map(({ item, trackingNumber }) =>
        updateDoc(doc(db, 'shipments', item.id), { trackingNumber, deliveryCarrier: carrierName })
      ))
      toast.success(`${targets.length}件の宅配便番号を反映しました（${carrierName}）`)
    } catch (e: any) {
      toast.error("CSVの取込に失敗しました: " + (e?.message || JSON.stringify(e)))
    } finally {
      setImportingShippingResult(false)
    }
  }

  // ヤマト運輸 B2クラウド送り状CSV作成
  const handleCreateYamatoB2Csv = () => {
    if (filteredItems.length === 0) {
      toast.warn("対象の出荷情報がありません")
      return
    }
    if (!window.confirm(`絞り込み結果（${filteredItems.length}件）の送り状CSVを作成します。よろしいですか？`)) return

    const rows = filteredItems.map((item: any) => buildYamatoB2CsvRow(item))
    const blob = generateYamatoB2Csv(rows)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `B2送り状_${dayjs().format('YYYYMMDD')}.csv`)
    link.click()
    URL.revokeObjectURL(url)
  }

  // 出荷先フィルター（sessionStorage 永続化）
  const [customerFilter, setCustomerFilter] = useState<string>(() => {
    return sessionStorage.getItem('shipmentListCustomerFilter') || ALL_CUSTOMERS
  })

  // 日付範囲フィルター
  const [dateRange, setDateRange] = useState(() => {
    const saved = sessionStorage.getItem('shipmentListDateRange')
    if (saved) {
      const parsed = JSON.parse(saved)
      return {
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null,
      }
    }
    return getInitialDateRange()
  })

  const handleCustomerChange = (value: string) => {
    setCustomerFilter(value)
    sessionStorage.setItem('shipmentListCustomerFilter', value)
  }

  // 宅配便番号フィルター（sessionStorage 永続化）
  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>(() => {
    return sessionStorage.getItem('shipmentListTrackingNumberFilter') || ''
  })

  const handleTrackingNumberChange = (value: string) => {
    setTrackingNumberFilter(value)
    sessionStorage.setItem('shipmentListTrackingNumberFilter', value)
  }

  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    const newRange = { ...dateRange, startDate: newDate }
    setDateRange(newRange)
    sessionStorage.setItem('shipmentListDateRange', JSON.stringify({
      startDate: newDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString(),
    }))
  }

  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    const newRange = { ...dateRange, endDate: newDate }
    setDateRange(newRange)
    sessionStorage.setItem('shipmentListDateRange', JSON.stringify({
      startDate: dateRange.startDate?.toISOString(),
      endDate: newDate?.toISOString(),
    }))
  }

  useEffect(() => {
    const q = query(collection(db, 'shipments'), orderBy("shippingDate", "desc"))
    const unsubscribe = onSnapshot(q, ({ docs }) => {
      setItems(docs.map((d) => ({ ...d.data(), id: d.id })))
    })
    return unsubscribe
  }, [])

  // 絞り込み済みアイテム（出荷先・期間）
  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        if (customerFilter !== ALL_CUSTOMERS && item.customer !== customerFilter) return false
        const shipDate = item.shippingDate ? dayjs(item.shippingDate.replace(/\//g, '-')) : null
        if (dateRange.startDate && (!shipDate || shipDate.isBefore(dateRange.startDate, 'day'))) return false
        if (dateRange.endDate && (!shipDate || shipDate.isAfter(dateRange.endDate, 'day'))) return false
        if (trackingNumberFilter && !(item.trackingNumber || '').includes(trackingNumberFilter)) return false
        return true
      })
      .map((item, i) => {
        const billingAmount = (Number(item.totalPrice) || 0) + (Number(item.shippingFee) || 0) - (Number(item.discount) || 0)
        return { No: i + 1, ...item, billingAmount }
      })
  }, [items, customerFilter, dateRange, trackingNumberFilter])

  // 集計
  const totalCount = filteredItems.length
  const totalPrice = useMemo(() => {
    return filteredItems.reduce((sum, item: any) => sum + (Number(item.billingAmount) || 0), 0)
  }, [filteredItems])

  const columns = isMobile ? [
    {
      field: "No",
      headerName: "No",
      width: 50,
      sortComparator: gridNumberComparator,
    },
    {
      field: "shippingDate",
      headerName: "出荷日",
      width: 100,
    },
    {
      field: "customer",
      headerName: "出荷先",
      flex: 1,
    },
    {
      field: "detail",
      headerName: "詳細",
      width: 70,
      renderCell: (params: any) => (
        <Chip label="詳細" color="default" size="small" onClick={() => navigate(`/admin/shipments/${params.row.id}`)} />
      ),
    },
    ...(currentUser?.isAdmin ? [{
      field: "delete",
      headerName: "削除",
      width: 70,
      renderCell: (params: any) => (
        <Chip label="削除" color="error" size="small" onClick={() => handleDelete(params.row.id)} />
      ),
    }] : []),
  ] : [
    {
      field: "No",
      headerName: "No",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "shippingDate",
      headerName: "出荷日",
      width: 120,
      cellClassName: "name-column--cell",
    },
    {
      field: "customer",
      headerName: "出荷先",
      flex: 1,
    },
    {
      field: "totalCount",
      headerName: "件数",
      width: 90,
      valueFormatter: (params: any) => params.value != null ? `${params.value}件` : '',
    },
    {
      field: "totalWeight",
      headerName: "総重量",
      width: 110,
      valueFormatter: (params: any) => {
        const grams = Number(params.value) || 0
        return `${(grams / 1000).toFixed(1)}kg`
      },
    },
    {
      field: "billingAmount",
      headerName: "総額（送料・値引き後）",
      width: 160,
      valueFormatter: (params: any) => {
        const yen = Number(params.value) || 0
        return `¥${yen.toLocaleString('ja-JP')}`
      },
    },
    {
      field: "deliveryCarrier",
      headerName: "宅配業者",
      width: 120,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: "trackingNumber",
      headerName: "宅配便番号",
      flex: 1,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: "detail",
      headerName: "詳細",
      width: 120,
      renderCell: (params: any) => (
        <Chip label="詳細" color="default" size="small" onClick={() => navigate(`/admin/shipments/${params.row.id}`)} />
      ),
    },
    ...(currentUser?.isAdmin ? [{
      field: "delete",
      headerName: "削除",
      width: 120,
      renderCell: (params: any) => (
        <Chip label="削除" color="error" size="small" onClick={() => handleDelete(params.row.id)} />
      ),
    }] : []),
  ]

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Typography variant={isMobile ? "h4" : "h3"}>出荷一覧</Typography>

      {/* フィルター */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="end" mt={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>出荷日期間</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
            <Box display="flex" alignItems="center" gap={1}>
              <DatePicker
                label="開始日"
                value={dateRange.startDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { size: 'small', sx: { width: '140px' } } }}
              />
              <Typography>～</Typography>
              <DatePicker
                label="終了日"
                value={dateRange.endDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { size: 'small', sx: { width: '140px' } } }}
              />
            </Box>
          </LocalizationProvider>
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>出荷先</Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="shipment-customer-filter-label">出荷先</InputLabel>
            <Select
              labelId="shipment-customer-filter-label"
              label="出荷先"
              size="small"
              value={customerFilter}
              onChange={(e) => handleCustomerChange(e.target.value as string)}
            >
              <MenuItem value={ALL_CUSTOMERS}>すべて</MenuItem>
              <MenuItem value="自社">自社</MenuItem>
              {customers.map((c: Customer) => (
                <MenuItem key={c.id ?? c.companyName} value={c.companyName}>{c.companyName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>宅配便番号</Typography>
          <TextField
            size="small"
            placeholder="宅配便番号で検索"
            value={trackingNumberFilter}
            onChange={(e) => handleTrackingNumberChange(e.target.value)}
            sx={{ minWidth: 200 }}
          />
        </Box>
      </Box>

      {/* 集計表示 */}
      <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
        <Box display="flex" gap={3} flexWrap="wrap" alignItems="center" justifyContent="space-between">
          <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
            <Typography variant="h6" color="primary">
              件数: {totalCount}件
            </Typography>
            <Typography variant="h6" color="success.main">
              合計金額（送料・値引き後）: ¥{totalPrice.toLocaleString('ja-JP')}
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="info"
              startIcon={<LocalShippingIcon />}
              onClick={handleCreateSagawaCsv}
              disabled={filteredItems.length === 0}
            >
              佐川急便 送り状CSV作成
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<LocalShippingIcon />}
              onClick={handleCreateYamatoB2Csv}
              disabled={filteredItems.length === 0}
            >
              ヤマト運輸 B2CSV作成
            </Button>
            <input
              type="file"
              accept=".csv"
              ref={shippingResultCsvInputRef}
              style={{ display: 'none' }}
              onChange={handleImportShippingResultChange}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleImportShippingResultClick}
              disabled={importingShippingResult}
            >
              {importingShippingResult ? '取込中...' : '出荷実績CSV取込'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ReceiptLongIcon />}
              onClick={handleIssueInvoices}
              disabled={issuingInvoices || filteredItems.length === 0}
            >
              {issuingInvoices ? '発行中...' : 'freeeに請求書を発行'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box
        mt={isMobile ? 1 : 2}
        height={isMobile ? "70vh" : "70vh"}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid rows={filteredItems} columns={columns} />
      </Box>

      {/* freee請求書発行結果 */}
      <Dialog open={!!invoiceResult} onClose={() => setInvoiceResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>freee請求書発行結果</DialogTitle>
        <DialogContent>
          {invoiceResult && invoiceResult.created.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle1" color="success.main">作成成功（{invoiceResult.created.length}件）</Typography>
              <List dense>
                {invoiceResult.created.map((c, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText
                      primary={`${c.customer}（出荷${c.shipmentCount}件 / ¥${c.amount.toLocaleString('ja-JP')}）`}
                      secondary={c.reportUrl ? (
                        <Link href={c.reportUrl} target="_blank" rel="noopener noreferrer">freeeで確認する</Link>
                      ) : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {invoiceResult && invoiceResult.skipped.length > 0 && (
            <Box>
              <Typography variant="subtitle1" color="warning.main">スキップ（{invoiceResult.skipped.length}件）</Typography>
              <List dense>
                {invoiceResult.skipped.map((s, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText
                      primary={`${s.customer}（出荷${s.shipmentCount}件）`}
                      secondary={s.reason}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {invoiceResult && invoiceResult.created.length === 0 && invoiceResult.skipped.length === 0 && (
            <Typography variant="body2">対象の取引先がありませんでした</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceResult(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ShipmentList
